import { Ban, Copy, FlaskConical, Plus, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { KpiTile, Note, Track } from '@/components/redesign/bits'
import { Button } from '@/components/ui/button'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { PageTitle } from '@/app/layout'
import { errorMessage } from '@/lib/api/errors'
import type { Discount, DiscountStatus } from '@/lib/api/types'
import { cn } from '@/lib/cn'
import { formatDate, formatMoney, formatRelative, numberFmt } from '@/lib/format'
import { useDiscounts, useRemoveDiscount, useSyncDiscounts } from './api'
import { DiscountDialog } from './discount-dialog'

const STATUS: Record<DiscountStatus, { label: string; tone: string }> = {
  active: { label: 'Active', tone: 'ok' },
  scheduled: { label: 'Starts later', tone: 'warn' },
  draft: { label: 'Draft', tone: 'warn' },
  expired: { label: 'Expired', tone: '' },
  removed: { label: 'Ended', tone: '' },
}

type View = 'active' | 'expired' | 'removed'

/** Which tab a code is listed under: codes that work now or will (drafts too), expired, ended. */
const viewOf = (d: Discount): View =>
  d.status === 'removed' ? 'removed' : d.status === 'expired' ? 'expired' : 'active'

const emptyText: Record<View, string> = {
  active: 'No active codes.',
  expired: 'No expired codes.',
  removed: 'No ended codes. Codes ended here or deleted in Lemon Squeezy are listed here.',
}

/**
 * Discount codes (redesign of 2026-10-16), as cards: what each takes off, what it works on, how
 * often it's been used against its limit, and what discounts took off payments overall. Codes are
 * created in Lemon Squeezy and used at checkout.
 */
export function DiscountsPage() {
  const discounts = useDiscounts()
  const remove = useRemoveDiscount()
  const sync = useSyncDiscounts()
  const [creating, setCreating] = useState(false)
  const [params, setParams] = useSearchParams()
  const meta = discounts.data?.meta
  const canEdit = meta?.can_edit ?? false
  const show = params.get('show')
  const view: View = show === 'expired' || show === 'removed' ? show : 'active'
  const all = discounts.data?.data ?? []
  const counts: Record<View, number> = { active: 0, expired: 0, removed: 0 }
  for (const d of all) counts[viewOf(d)]++
  const rows = all.filter((d) => viewOf(d) === view)
  const used = all.reduce((sum, d) => sum + (d.redemptions ?? 0), 0)
  const given = meta?.given
  const setView = (next: View) =>
    setParams(
      (p) => {
        if (next === 'active') p.delete('show')
        else p.set('show', next)
        return p
      },
      { replace: true },
    )

  return (
    <>
      <PageTitle
        title="Discounts"
        description="Codes customers enter at checkout, for a new plan, an upgrade or an add-on. Codes made here are created in Lemon Squeezy too. Lemon Squeezy can’t edit a code or apply one to a plan someone already pays for, so end it and make a new one instead; for a loyal customer, a complimentary plan on their customer page is the other option."
        actions={
          canEdit && (
            <>
              <Button
                variant="secondary"
                loading={sync.isPending}
                onClick={() =>
                  sync.mutate(undefined, {
                    onSuccess: ({ synced: s }) =>
                      toast.success(
                        s.added + s.updated + s.removed === 0
                          ? 'Already in step with Lemon Squeezy.'
                          : `Synced: ${s.added} added, ${s.updated} updated, ${s.removed} removed in Lemon Squeezy.`,
                      ),
                    onError: (e) => toast.error(errorMessage(e)),
                  })
                }
              >
                <RefreshCw aria-hidden="true" /> Sync now
              </Button>
              <Button onClick={() => setCreating(true)}>
                <Plus aria-hidden="true" /> New discount
              </Button>
            </>
          )
        }
      />
      {meta?.test_mode && (
        <Note icon={<FlaskConical aria-hidden="true" />} title="Billing is in test mode">
          Codes made now only work at test checkouts.
        </Note>
      )}

      {discounts.isPending ? (
        <Skeleton className="h-64 rounded-[14px]" />
      ) : discounts.isError ? (
        <div className="card">
          <ErrorState error={discounts.error} onRetry={() => void discounts.refetch()} />
        </div>
      ) : (
        <>
          <div className="kpis">
            <KpiTile
              label="Active codes"
              value={numberFmt.format(counts.active)}
              compare={`${counts.expired} expired · ${counts.removed} ended`}
            />
            <KpiTile
              label="Times used"
              value={numberFmt.format(used)}
              compare={`all codes${meta?.synced_at ? `, synced ${formatRelative(meta.synced_at)}` : ''}`}
            />
            <KpiTile
              label="Taken off this month"
              value={formatMoney(given?.this_month ?? 0)}
              now={given?.this_month}
              previous={given?.last_month}
              invert
              compare={`${numberFmt.format(given?.payments_this_month ?? 0)} discounted ${given?.payments_this_month === 1 ? 'payment' : 'payments'}, vs last month`}
            />
            <KpiTile
              label="Taken off, all time"
              value={formatMoney(given?.all_time ?? 0)}
              compare="before tax, all codes together"
            />
          </div>

          {all.length > 0 && (
            <div className="fbar">
              <Segmented<View>
                label="Show codes"
                value={view}
                onChange={setView}
                options={[
                  { value: 'active', label: 'Active', count: counts.active },
                  ...(counts.expired > 0 || view === 'expired'
                    ? [{ value: 'expired' as const, label: 'Expired', count: counts.expired }]
                    : []),
                  { value: 'removed', label: 'Ended', count: counts.removed },
                ]}
              />
              <span style={{ fontSize: 12.5, color: 'var(--subtle)' }}>
                Codes made or deleted in Lemon Squeezy’s dashboard, and how often each is used, sync every
                hour.
              </span>
            </div>
          )}

          {all.length === 0 ? (
            <div className="card pad">
              <p className="empty-line">
                No discount codes yet.{' '}
                {canEdit
                  ? 'Make one for a launch offer or a loyal customer.'
                  : 'Owners and admins make them.'}
              </p>
            </div>
          ) : rows.length === 0 ? (
            <div className="card pad">
              <p className="empty-line">{emptyText[view]}</p>
            </div>
          ) : (
            <div className="cards">
              {rows.map((d) => (
                <DiscountCard
                  key={d.id}
                  d={d}
                  canEdit={canEdit}
                  ending={remove.isPending && remove.variables === d.id}
                  onEnd={() =>
                    remove.mutate(d.id, {
                      onSuccess: () => toast.success(`${d.code} no longer works.`),
                      onError: (e) => toast.error(errorMessage(e)),
                    })
                  }
                />
              ))}
            </div>
          )}
        </>
      )}
      {creating && <DiscountDialog open onOpenChange={setCreating} />}
    </>
  )
}

function DiscountCard({
  d,
  canEdit,
  ending,
  onEnd,
}: {
  d: Discount
  canEdit: boolean
  ending: boolean
  onEnd: () => void
}) {
  const status = STATUS[d.status]
  const used = d.redemptions ?? 0
  const ratio = d.max_redemptions ? used / d.max_redemptions : 0
  const worksOn =
    d.applies_to?.join(', ') ??
    (d.limited_to_products ? 'Some products (set in Lemon Squeezy)' : 'Everything')
  const dates = [
    d.valid_from ? `from ${formatDate(d.valid_from)}` : null,
    d.expires_at ? `until ${formatDate(d.expires_at)}` : 'no end date',
  ]
    .filter(Boolean)
    .join(d.expires_at ? ' ' : ', ')

  return (
    <article className="card dc" style={viewOf(d) === 'active' ? undefined : { opacity: 0.75 }}>
      <div className="bar">
        <span className="code">{d.code}</span>
        <span className="push">
          <span className={cn('chip', status.tone)}>{status.label}</span>
        </span>
      </div>
      <div>
        <b style={{ fontSize: 15 }}>{d.summary}</b>
        <div style={{ fontSize: 12.5, color: 'var(--subtle)' }}>
          {worksOn} · {dates}
        </div>
      </div>
      <div>
        <div className="dc-row">
          <span>Used</span>
          <b>
            {numberFmt.format(used)}
            {d.max_redemptions !== null && ` of ${numberFmt.format(d.max_redemptions)}`}
          </b>
        </div>
        {d.max_redemptions !== null && (
          <div style={{ marginTop: 6 }}>
            <Track ratio={ratio} tone={ratio > 0.85 ? 'warn' : ''} />
          </div>
        )}
      </div>
      <div className="dc-row">
        <span>For</span>
        <b>{d.customer ? <Link to={`/customers/${d.customer.id}`}>{d.customer.name}</Link> : 'Anyone'}</b>
      </div>
      <div style={{ fontSize: 12, color: 'var(--subtle)' }}>
        {d.name}
        {d.source === 'lemon_squeezy'
          ? ' · made in Lemon Squeezy'
          : d.created_by
            ? ` · by ${d.created_by}`
            : ''}
      </div>
      <div className="bar" style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}>
        <button
          type="button"
          className="btn sm ghost"
          aria-label={`Copy ${d.code}`}
          onClick={() => void navigator.clipboard.writeText(d.code).then(() => toast.success('Code copied.'))}
        >
          <Copy aria-hidden="true" />
          Copy
        </button>
        {canEdit && d.status !== 'removed' && (
          <button type="button" className="btn sm ghost danger push" disabled={ending} onClick={onEnd}>
            <Ban aria-hidden="true" />
            End it
          </button>
        )}
      </div>
    </article>
  )
}
